<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <RouterLink to="/" aria-label="Visit home page" class="app-logo">
      <VuesticLogo />
    </RouterLink>

    <VaAccordion v-if="!isLoading && !error && routes.length" v-model="value" multiple>
      <VaCollapse v-for="(route, index) in routes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <InlineSvg v-if="route.meta.icon" :icon="route.meta.icon" width="20" height="20" />
              <VaSidebarItemTitle
                class="flex justify-between items-center leading-5 font-semibold"
                :data-lang="route.displayName"
              >
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold" :data-lang="childRoute.displayName">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { fetchMenuBasedOnPermissions } from '@/services/menuService'
import VuesticLogo from '@/components/VuesticLogo.vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  components: {
    VuesticLogo,
  },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const routes = ref<INavigationRoute[]>([])
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.startsWith(`/${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '240px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))
    const isLoading = ref<boolean>(false)
    const error = ref<string | null>(null)

    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const loadMenu = async () => {
      isLoading.value = true
      error.value = null
      try {
        routes.value = await fetchMenuBasedOnPermissions()
      } catch (err) {
        console.error('Error fetching menu:', err)
        error.value = 'Unable to load menu.'
      } finally {
        isLoading.value = false
      }
    }

    onMounted(() => {
      loadMenu()
    })

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      arrowDirection,
      routes,
      isLoading,
      error,
      VuesticLogo,
    }
  },
})
</script>
